import {
  Profile,
  Roles,
  Scope,
  urls,
  Users,
} from '@motional-cc/fe/interface/api/user-profile-service';
import { captureException } from '@sentry/react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import compact from 'lodash/compact';
import { useCallback, useMemo } from 'react';
import { useApi, useInfiniteApi, useMutateApi } from 'src/api/hooks/service';
import { user as userHost } from 'src/api/hosts';
import { useMessages } from 'src/contexts/messages';
import { ProfileSettings } from 'src/contexts/profile-settings/types';
import { isIncompleteStatus } from 'src/interface/command-center/unsorted-type-guards';
import { newLine } from 'src/tools/entities';
import { updateErrorMessage } from 'src/tools/object/updateErrorMessage';
import { convertToAbsolutePaths } from './utils';

// Roles which have a static name
const STATIC_ROLES = ['field_support_specialist'] as const;
type StaticRole = (typeof STATIC_ROLES)[number];

export const userPaths = convertToAbsolutePaths(userHost, urls);

const useGetRoles = () => {
  const response = useInfiniteApi<Roles.GetRoles.ResponseBody>(
    userPaths.Roles.GetRoles(),
    {
      isKnownToBeAnIrregularApi: true,
    },
  );

  return {
    roles: response?.result,
    ...response,
  };
};

const useGetHasScopes = () => {
  const { userProfile } = useUserProfile();
  // strigifying to prevent extra renders across the app
  const scopesString =
    userProfile?.scopes ?
      JSON.stringify([...userProfile.scopes].sort())
    : 'null';

  return useCallback(
    (
      scopesToCheck: ReadonlyArray<Scope> = [],
      { eitherScope = false }: { eitherScope?: boolean } = {},
    ) => {
      const scopes = JSON.parse(scopesString);

      if (!scopes) {
        return null;
      }

      if (scopesToCheck.length === 0) {
        return true;
      }

      return scopesToCheck[eitherScope ? 'some' : 'every'](
        (scope) => !!scopes?.includes(scope),
      );
    },
    [scopesString],
  );
};

const useHasScopes = (
  scopesToCheck: ReadonlyArray<Scope> = [],
  { eitherScope = false }: { eitherScope?: boolean } = {},
) => {
  const hasScopes = useGetHasScopes();

  return hasScopes(scopesToCheck, { eitherScope });
};

const useHasRole = (roleId: StaticRole) => {
  const { userProfile } = useUserProfile();
  const { result: assignedRoles, status } =
    useApi<Users.GetUserRoles.ResponseBody>(
      userPaths.Users.GetUserRoles(userProfile?.id ?? ''),
      { enabled: !!userProfile?.id },
    );

  if (!assignedRoles || isIncompleteStatus(status)) {
    return null;
  }

  return assignedRoles?.some((assignedRole) => assignedRole.id === roleId);
};

const queryKey = [userPaths.Profile.GetProfile()];
const useUserProfile = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { result: userProfile, ...rest } = useApi<
    Profile.GetProfile.ResponseBody,
    Profile.GetProfile.RequestQuery
  >(userPaths.Profile.GetProfile(), {
    queryKey,
    refetchOnWindowFocus: 'always',
    enabled,
  });

  return { userProfile, ...rest };
};

const syncUserProfile = (queryClient: QueryClient) => {
  queryClient.refetchQueries({
    queryKey: ['api', ...queryKey],
  });
};

const useMutateUserProfile = ({
  notifyStyle = 'noisy',
  failureStyle = notifyStyle === 'silent' ? 'fail-silently' : 'notify-on-fail',
  successStyle = notifyStyle === 'silent' ? 'succeed-silently' : (
    'notify-on-success'
  ),
}:
  | {
      notifyStyle?: 'noisy' | 'silent';
      failureStyle?: never;
      successStyle?: never;
    }
  | {
      notifyStyle?: 'manual';
      failureStyle?: 'notify-on-fail' | 'fail-silently';
      successStyle?: 'notify-on-success' | 'succeed-silently';
    } = {}) => {
  const { showMessage } = useMessages();
  const queryClient = useQueryClient();
  const mutator = useMutateApi<
    Profile.PatchProfile.ResponseBody,
    Profile.PatchProfile.RequestBody
  >(userPaths.Profile.PatchProfile(), 'PATCH');

  // I’m not sure why rule of hooks won’t let me use `mutator.mutate` as a dep
  const originalMutate = mutator.mutate;
  const mutate = useCallback(
    async <T extends Omit<Profile.PatchProfile.RequestBody, 'version'>>(
      dataLabel: string,
      updatedProfile: T,
      ...args: T extends { settings: ProfileSettings } ?
        [options: { ALLOW_SETTINGS_SAVE: true }]
      : [options?: { ALLOW_SETTINGS_SAVE?: boolean }]
    ) => {
      const options = args?.at(0);
      if (updatedProfile.settings && !options?.ALLOW_SETTINGS_SAVE) {
        throw new Error(
          'Do not save settings directly. Use useUserProfileSetting hook instead.',
        );
      }

      try {
        const mutationResponse = await originalMutate({
          ...updatedProfile,
        });

        if (mutationResponse.result?.settings) {
          queryClient.setQueryData<Profile.GetProfile.ResponseBody>(
            ['api', ...queryKey],
            (currentProfile) => {
              if (!currentProfile) return;
              return {
                ...currentProfile,
                result: {
                  ...currentProfile.result,
                  settings: mutationResponse.result.settings,
                },
              };
            },
          );
        }
        // Our APIs are too inconsistent to trust building ONLY from mutation responses
        syncUserProfile(queryClient);

        if (successStyle === 'notify-on-success') {
          showMessage({
            type: 'success',
            title: 'Settings updated successfully',
            description: dataLabel,
          });
        }
        return mutationResponse;
      } catch (_error) {
        const error = _error as Error;
        if (failureStyle === 'notify-on-fail') {
          showMessage({
            type: 'error',
            title: 'Failed to update settings',
            description: error.message,
          });
        }
        if (failureStyle !== 'fail-silently') {
          captureException(
            updateErrorMessage(error, (message) =>
              compact([
                `Failed to save important user settings: ${dataLabel}`,
                message,
              ]).join(`${newLine}  - `),
            ),
          );
        }
        throw error;
      }
    },
    [failureStyle, originalMutate, queryClient, showMessage, successStyle],
  );

  return useMemo(
    () => ({
      mutate,
      isLoading: mutator.isLoading,
      error: mutator.error,
    }),
    [mutate, mutator.isLoading, mutator.error],
  );
};

export const userApi = {
  useGetHasScopes,
  useHasScopes,
  useUserProfile,
  useMutateUserProfile,
  syncUserProfile,
  useGetRoles,
  useHasRole,
};
